import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { MessageCircle, AlertCircle, Wind } from 'lucide-react';
import { Button } from './ui/button';
import Header from './Header';
import Footer from './Footer';
import FloatingWhatsApp from './FloatingWhatsApp';

const BlogPuliziaFiltriCondizionatore = () => {
  const handleWhatsApp = () => {
    window.open('https://wa.me/393287686356?text=Ciao,%20ho%20bisogno%20di%20assistenza%20per%20il%20condizionatore.', '_blank');
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Come Pulire i Filtri del Condizionatore da Soli | Guida SOS Casa Roma</title>
        <meta name="description" content="Guida completa per pulire i filtri del condizionatore fai da te. Migliora efficienza e aria salubre. Assistenza condizionatori H24 Roma. Tel: 328 768 6356" />
        <link rel="canonical" href={`${window.location.origin}/blog/pulire-filtri-condizionatore`} />
      </Helmet>
      
      <div className="min-h-screen bg-gray-50">
        <Header />
        
        <article className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-lg p-8">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Come Pulire i Filtri del Condizionatore da Soli
              </h1>
              
              <div className="prose max-w-none">
                <p className="text-lg text-gray-700 mb-6">
                  Pulire i filtri del condizionatore è un'operazione semplice che puoi fare da solo. Migliora l'efficienza, riduce i consumi e garantisce aria più pulita.
                </p>

                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
                  <div className="flex items-start">
                    <Wind className="h-6 w-6 text-blue-500 mr-3 flex-shrink-0 mt-1" />
                    <p className="text-gray-800">
                      <strong>Quando pulire:</strong> Ogni 2-3 settimane durante l'estate, o quando noti aria debole e polvere sul filtro.
                    </p>
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                  Perché È Importante Pulire i Filtri
                </h2>
                <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
                  <li>Migliora l'efficienza del condizionatore (risparmio energetico fino al 15%)</li>
                  <li>Aria più pulita e salubre (rimuove polvere, pollini, batteri)</li>
                  <li>Evita cattivi odori e proliferazione di muffe</li>
                  <li>Prolunga la vita del climatizzatore</li>
                  <li>Previene malfunzionamenti e costose riparazioni</li>
                </ul>

                <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                  Cosa Ti Serve
                </h2>
                <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
                  <li>Scala o sgabello (per raggiungere lo split)</li>
                  <li>Aspirapolvere con spazzola morbida</li>
                  <li>Acqua tiepida e sapone neutro (o detersivo per piatti)</li>
                  <li>Asciugamano pulito</li>
                  <li>Spray igienizzante specifico (opzionale ma consigliato)</li>
                </ul>

                <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                  Procedura Passo Passo
                </h2>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-700 text-white rounded-full flex items-center justify-center font-bold">1</div>
                    <div>
                      <p className="font-semibold text-gray-900">Spegni il condizionatore</p>
                      <p className="text-gray-700">Spegni completamente il clima e stacca la spina o togli corrente dal quadro elettrico per sicurezza.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-700 text-white rounded-full flex items-center justify-center font-bold">2</div>
                    <div>
                      <p className="font-semibold text-gray-900">Apri il pannello frontale</p>
                      <p className="text-gray-700">Solleva delicatamente il coperchio superiore dello split (su alcuni modelli si sgancia premendo i fermi laterali).</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-700 text-white rounded-full flex items-center justify-center font-bold">3</div>
                    <div>
                      <p className="font-semibold text-gray-900">Rimuovi i filtri</p>
                      <p className="text-gray-700">Estrai i filtri tirandoli verso il basso o verso di te (dipende dal modello). Di solito sono 1 o 2 griglie di plastica.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-700 text-white rounded-full flex items-center justify-center font-bold">4</div>
                    <div>
                      <p className="font-semibold text-gray-900">Aspira la polvere</p>
                      <p className="text-gray-700">Passa l'aspirapolvere su entrambi i lati del filtro per rimuovere la polvere superficiale.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-700 text-white rounded-full flex items-center justify-center font-bold">5</div>
                    <div>
                      <p className="font-semibold text-gray-900">Lava con acqua</p>
                      <p className="text-gray-700">Lava i filtri sotto acqua corrente tiepida con sapone neutro. Strofina delicatamente con le dita o una spugnetta morbida.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-700 text-white rounded-full flex items-center justify-center font-bold">6</div>
                    <div>
                      <p className="font-semibold text-gray-900">Risciacqua e asciuga</p>
                      <p className="text-gray-700">Risciacqua abbondantemente e lascia asciugare COMPLETAMENTE all'ombra (mai al sole diretto). Aspetta almeno 2-3 ore.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-700 text-white rounded-full flex items-center justify-center font-bold">7</div>
                    <div>
                      <p className="font-semibold text-gray-900">Igienizza lo split (opzionale)</p>
                      <p className="text-gray-700">Mentre aspetti che i filtri asciughino, spruzza prodotto igienizzante nelle alette interne dello split.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-700 text-white rounded-full flex items-center justify-center font-bold">8</div>
                    <div>
                      <p className="font-semibold text-gray-900">Rimonta tutto</p>
                      <p className="text-gray-700">Una volta asciutti, reinserisci i filtri nello split e richiudi il pannello. Riaccendi il condizionatore.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-orange-50 border-l-4 border-orange-500 p-4 mb-6">
                  <div className="flex items-start">
                    <AlertCircle className="h-6 w-6 text-orange-500 mr-3 flex-shrink-0 mt-1" />
                    <p className="text-gray-800">
                      <strong>Attenzione:</strong> Non montare MAI filtri bagnati o umidi. Rischi muffe, cattivi odori e danni al condizionatore.
                    </p>
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                  Quando Serve la Pulizia Professionale
                </h2>
                <p className="text-gray-700 mb-4">
                  Chiama SOS Casa Roma per:
                </p>
                <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
                  <li>Sanificazione completa con vapore e prodotti specifici</li>
                  <li>Pulizia delle alette interne e batteria (ogni 1-2 anni)</li>
                  <li>Cattivi odori persistenti dopo la pulizia filtri</li>
                  <li>Controllo perdite acqua o gas refrigerante</li>
                  <li>Manutenzione completa prima dell'estate</li>
                </ul>

                <div className="bg-blue-600 rounded-lg p-8 text-center mt-12">
                  <h3 className="text-2xl font-bold text-white mb-4">
                    Vuoi una Pulizia Professionale del Condizionatore?
                  </h3>
                  <p className="text-white text-lg mb-6">
                    Sanificazione completa, controllo gas R32, pulizia batterie. Tecnici certificati FGAS disponibili H24 a Roma.
                  </p>
                  <Button
                    onClick={handleWhatsApp}
                    size="lg"
                    className="bg-orange-500 hover:bg-orange-600 text-white text-xl px-10 py-7 rounded-lg shadow-lg transition-all duration-300 hover:scale-105"
                  >
                    <MessageCircle className="mr-3 h-7 w-7" />
                    Chiama SOS Casa Roma su WhatsApp
                  </Button>
                  <p className="text-white mt-4 font-semibold">
                    📞 328 768 6356
                  </p>
                </div>
              </div>
            </div>
          </div>
        </article>

        <Footer />
        <FloatingWhatsApp />
      </div>
    </>
  );
};

export default BlogPuliziaFiltriCondizionatore;
